import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  IconButton,
  Collapse,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  DirectionsCar as CarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  LocalOffer as PriceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useBooking } from '../contexts/BookingContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiService } from '../services/api.js';

const ProductSelectionPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  
  const { 
    selectedGarage,
    vehicleRegistration,
    vehicleData,
    availableProducts,
    availableAddons,
    selectedProducts,
    selectedAddons,
    subtotal,
    totalAmount,
    setAvailableProducts,
    setAvailableAddons,
    setSelectedProducts,
    setSelectedAddons,
    updatePricing,
    setLoading,
    setError,
    clearError,
    isLoading,
    error 
  } = useBooking();

  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedAddons, setExpandedAddons] = useState({});

  // Redirect if no required data
  useEffect(() => {
    if (!vehicleRegistration || !selectedGarage) {
      navigate('/');
      return;
    }
    fetchGarageProducts();
  }, [selectedGarage, vehicleRegistration, navigate]);

  // Calculate pricing when selections change
  useEffect(() => {
    calculatePricing();
  }, [selectedProducts, selectedAddons, selectedGarage]);

  const fetchGarageProducts = async () => {
    try {
      setLoading(true);
      clearError();
      
      const [productsResponse, addonsResponse] = await Promise.all([
        apiService.get(`/garages/${selectedGarage.id}/products`),
        apiService.get(`/garages/${selectedGarage.id}/addons`)
      ]);
      
      setAvailableProducts(productsResponse.data);
      setAvailableAddons(addonsResponse.data);
      
      // Auto-select MOT if available
      const motProduct = productsResponse.data.find(p => p.product.category === 'MOT');
      if (motProduct) {
        setSelectedProducts([motProduct]);
      }
      
    } catch (err) {
      console.error('Error fetching garage products:', err);
      setError('Failed to load available services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = () => {
    let subtotal = 0;
    
    // Calculate products total
    selectedProducts.forEach(product => {
      subtotal += parseFloat(product.base_price);
    });
    
    // Calculate addons total
    selectedAddons.forEach(addon => {
      if (!addon.is_free) {
        subtotal += parseFloat(addon.base_price);
      }
    });
    
    // For now, no discounts applied here - will be applied at appointment time
    updatePricing({
      subtotal,
      discountAmount: 0,
      totalAmount: subtotal
    });
  };

  const handleProductToggle = (garageProduct) => {
    const isSelected = selectedProducts.some(p => p.product_id === garageProduct.product_id);
    
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.product_id !== garageProduct.product_id));
    } else {
      setSelectedProducts([...selectedProducts, garageProduct]);
    }
  };

  const handleAddonToggle = (garageAddon) => {
    const isSelected = selectedAddons.some(a => a.addon_id === garageAddon.addon_id);
    
    if (isSelected) {
      setSelectedAddons(selectedAddons.filter(a => a.addon_id !== garageAddon.addon_id));
    } else {
      setSelectedAddons([...selectedAddons, garageAddon]);
    }
  };

  const toggleProductExpansion = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const toggleAddonExpansion = (addonId) => {
    setExpandedAddons(prev => ({
      ...prev,
      [addonId]: !prev[addonId]
    }));
  };

  const handleContinue = () => {
    if (selectedProducts.length === 0) {
      setError('Please select at least one service before continuing.');
      return;
    }
    navigate('/appointment');
  };

  if (isLoading && availableProducts.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate('/garages')} edge="start">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Select Services
          </Typography>
        </Box>
        
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            Home
          </Link>
          <Link color="inherit" onClick={() => navigate('/garages')} sx={{ cursor: 'pointer' }}>
            Garages
          </Link>
          <Typography color="text.primary">Services</Typography>
        </Breadcrumbs>

        {/* Garage Info */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" fontWeight="600" color="primary.main">
                {selectedGarage?.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {selectedGarage?.address}, {selectedGarage?.postcode}
                </Typography>
              </Box>
              {selectedGarage?.rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <Rating value={selectedGarage.rating} precision={0.1} size="small" readOnly />
                  <Typography variant="body2" color="text.secondary">
                    ({selectedGarage.total_reviews || 0})
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Vehicle Info */}
        <Card sx={{ mb: 3, bgcolor: 'success.50' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CarIcon color="success" />
              <Typography variant="h6" fontWeight="600" color="success.dark">
                {vehicleRegistration}
              </Typography>
              {vehicleData && (
                <Typography variant="body2" color="text.secondary">
                  {vehicleData.make} {vehicleData.model} ({vehicleData.year})
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Products & Services */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={4}>
            {/* Products Section */}
            <Box>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Services & Tests
                <Chip label={`${selectedProducts.length} selected`} size="small" color="primary" />
              </Typography>
              
              <Stack spacing={2}>
                {availableProducts.map(garageProduct => {
                  const product = garageProduct.product;
                  const isSelected = selectedProducts.some(p => p.product_id === garageProduct.product_id);
                  const isExpanded = expandedProducts[product.id];
                  const isMOT = product.category === 'MOT';
                  
                  return (
                    <Card 
                      key={garageProduct.product_id}
                      variant={isSelected ? "outlined" : "elevation"}
                      sx={{ 
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleProductToggle(garageProduct)}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6">{product.name}</Typography>
                                    <Chip 
                                      label={product.category} 
                                      size="small" 
                                      color={isMOT ? "error" : "primary"} 
                                      variant="outlined" 
                                    />
                                    {isMOT && <Chip label="Required" size="small" color="error" />}
                                  </Box>
                                }
                                sx={{ m: 0 }}
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
                              {product.description}
                            </Typography>
                            
                            {product.includes && product.includes.length > 0 && (
                              <Box sx={{ ml: 4 }}>
                                <Button
                                  onClick={() => toggleProductExpansion(product.id)}
                                  size="small"
                                  startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  sx={{ mb: 1 }}
                                >
                                  What's included ({product.includes.length} items)
                                </Button>
                                
                                <Collapse in={isExpanded}>
                                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                                    <Grid container spacing={1}>
                                      {product.includes.map((item, index) => (
                                        <Grid item xs={12} sm={6} key={index}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckIcon fontSize="small" color="success" />
                                            <Typography variant="body2">{item}</Typography>
                                          </Box>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </Box>
                            )}
                          </Box>
                          
                          <Box sx={{ ml: 2, textAlign: 'right' }}>
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              £{parseFloat(garageProduct.base_price).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {product.duration_minutes} mins
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>

            {/* Add-ons Section */}
            {availableAddons.length > 0 && (
              <Box>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Additional Services
                  <Chip label={`${selectedAddons.length} selected`} size="small" color="secondary" />
                </Typography>
                
                <Stack spacing={2}>
                  {availableAddons.map(garageAddon => {
                    const addon = garageAddon.addon;
                    const isSelected = selectedAddons.some(a => a.addon_id === garageAddon.addon_id);
                    const isExpanded = expandedAddons[addon.id];
                    
                    return (
                      <Card 
                        key={garageAddon.addon_id}
                        variant="outlined"
                        sx={{ 
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'secondary.main' : 'divider',
                          bgcolor: isSelected ? 'secondary.50' : 'background.paper'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleAddonToggle(garageAddon)}
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="h6">{addon.name}</Typography>
                                      {garageAddon.is_free && <Chip label="Free" size="small" color="success" />}
                                    </Box>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
                                {addon.description}
                              </Typography>
                              
                              {addon.includes && addon.includes.length > 0 && (
                                <Box sx={{ ml: 4 }}>
                                  <Button
                                    onClick={() => toggleAddonExpansion(addon.id)}
                                    size="small"
                                    startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{ mb: 1 }}
                                  >
                                    What's included ({addon.includes.length} items)
                                  </Button>
                                  
                                  <Collapse in={isExpanded}>
                                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                                      <Grid container spacing={1}>
                                        {addon.includes.map((item, index) => (
                                          <Grid item xs={12} sm={6} key={index}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <CheckIcon fontSize="small" color="success" />
                                              <Typography variant="body2">{item}</Typography>
                                            </Box>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </Box>
                                  </Collapse>
                                </Box>
                              )}
                            </Box>
                            
                            <Box sx={{ ml: 2, textAlign: 'right' }}>
                              <Typography variant="h6" color="secondary.main" fontWeight="bold">
                                {garageAddon.is_free ? 'Free' : `£${parseFloat(garageAddon.base_price).toFixed(2)}`}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Summary Sidebar */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 24 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Booking Summary
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Selected Products */}
              {selectedProducts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Services ({selectedProducts.length})
                  </Typography>
                  {selectedProducts.map(product => (
                    <Box key={product.product_id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{product.product.name}</Typography>
                      <Typography variant="body2" fontWeight="500">
                        £{parseFloat(product.base_price).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* Selected Add-ons */}
              {selectedAddons.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Add-ons ({selectedAddons.length})
                  </Typography>
                  {selectedAddons.map(addon => (
                    <Box key={addon.addon_id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{addon.addon.name}</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {addon.is_free ? 'Free' : `£${parseFloat(addon.base_price).toFixed(2)}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary.main" fontWeight="bold">
                  £{totalAmount.toFixed(2)}
                </Typography>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                * Final price may vary based on appointment time and any applicable discounts
              </Typography>
              
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleContinue}
                disabled={selectedProducts.length === 0}
                sx={{ mt: 2 }}
              >
                Continue to Appointment
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductSelectionPage;